import { supabase } from '@/integrations/supabase/client';

type ValidationResult = 'valid' | 'invalid' | 'unknown';

interface CachedResult {
  result: ValidationResult;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

const CACHE_KEY = 'platform_admin_validation';
const DEFAULT_TTL = 10 * 60 * 1000; // 10 minutes

class PlatformAdminValidator {
  private cache: Map<string, CachedResult> = new Map();
  /** Validations on the wire, by user: callers that arrive meanwhile share the answer instead of asking again. */
  private inflight: Map<string, Promise<ValidationResult>> = new Map();

  private getCachedResult(userId: string): ValidationResult | null {
    const cached = this.cache.get(`${CACHE_KEY}_${userId}`);
    if (!cached) return null;
    
    const now = Date.now();
    if (now - cached.timestamp > cached.ttl) {
      this.cache.delete(`${CACHE_KEY}_${userId}`);
      return null;
    }
    
    return cached.result;
  }

  private setCachedResult(userId: string, result: ValidationResult, ttl: number = DEFAULT_TTL) {
    this.cache.set(`${CACHE_KEY}_${userId}`, {
      result,
      timestamp: Date.now(),
      ttl
    });
  }

  validatePlatformAdmin(userId?: string): Promise<ValidationResult> {
    if (!userId) return this.validateNow(userId);
    const pending = this.inflight.get(userId);
    if (pending) return pending;
    const request = this.validateNow(userId).finally(() => this.inflight.delete(userId));
    this.inflight.set(userId, request);
    return request;
  }

  private async validateNow(userId?: string): Promise<ValidationResult> {
    if (!userId) {
      console.log('[PlatformAdminValidator] No user ID provided');
      return 'invalid';
    }

    // Check cache first
    const cached = this.getCachedResult(userId);
    if (cached) {
      console.log(`[PlatformAdminValidator] Using cached result: ${cached}`);
      return cached;
    }

    try {
      console.log('[PlatformAdminValidator] Making validation request...');
      const { data, error } = await supabase.rpc('validate_platform_admin_session');
      
      if (error) {
        console.error('[PlatformAdminValidator] RPC error:', error);
        // Cache as unknown for shorter time on errors
        this.setCachedResult(userId, 'unknown', 2 * 60 * 1000); // 2 minutes
        return 'unknown';
      }

      // Handle both boolean and object return types
      let isValid = false;
      if (typeof data === 'boolean') {
        isValid = data;
      } else if (data && typeof data === 'object') {
        isValid = (data as Record<string, unknown>)?.valid === true;
      }

      const result: ValidationResult = isValid ? 'valid' : 'invalid';
      console.log(`[PlatformAdminValidator] Validation result: ${result}`);
      
      // Cache successful results for longer
      this.setCachedResult(userId, result, DEFAULT_TTL);
      return result;
      
    } catch (error) {
      console.error('[PlatformAdminValidator] Network error:', error);
      // Cache as unknown for shorter time on network errors
      this.setCachedResult(userId, 'unknown', 2 * 60 * 1000); // 2 minutes
      return 'unknown';
    }
  }

  clearCache(userId?: string) {
    if (userId) {
      this.cache.delete(`${CACHE_KEY}_${userId}`);
    } else {
      this.cache.clear();
    }
  }
}

export const platformAdminValidator = new PlatformAdminValidator();
export type { ValidationResult };